import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { defaultStyle } from "components/Modals";
import { ProtectedPaths } from "routes";
import { getClients } from "api/resources/clients";
import {
  createClientGroup,
  updateClientGroup,
} from "api/resources/clientGroups";
import { ClientGroup, ClientGroupMember } from "api/models/ClientGroup";

type ClientGroupModalProps = {
  group?: ClientGroup;
  allGroups: ClientGroup[];
  onSaved: () => void;
  onClose: () => void;
};

const clientName = (client: {
  type: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
}) =>
  client.type === "BUSINESS"
    ? client.companyName
    : `${client.firstName} ${client.lastName}`;

const ClientGroupModal = ({
  group,
  allGroups,
  onSaved,
  onClose,
}: ClientGroupModalProps) => {
  const [name, setName] = useState(group?.name ?? "");
  const [visibleToClients, setVisibleToClients] = useState(
    group?.visibleToClients ?? false,
  );
  const [members, setMembers] = useState<ClientGroupMember[]>(
    group?.clients ?? [],
  );
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ClientGroupMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    const delay = setTimeout(() => {
      getClients(0, 15, {}, query)
        .then((result) =>
          setSearchResults(
            result?.data.content.map((client) => ({
              id: client.id,
              name: clientName(client),
            })) ?? [],
          ),
        )
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(delay);
  }, [query]);

  const memberIds = new Set(members.map((member) => member.id));
  const otherGroupNames = (clientId: number) =>
    allGroups
      .filter(
        (other) =>
          other.id !== group?.id &&
          other.clients.some((client) => client.id === clientId),
      )
      .map((other) => other.name)
      .join(", ");

  const addMember = (client: ClientGroupMember | null) => {
    if (client && !memberIds.has(client.id)) {
      setMembers([...members, client]);
    }
    setQuery("");
  };

  const removeMember = (clientId: number) =>
    setMembers(members.filter((member) => member.id !== clientId));

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const data = {
      name: name.trim(),
      visibleToClients,
      clientIds: members.map((member) => member.id),
    };

    (group ? updateClientGroup(group.id, data) : createClientGroup(data))
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((err) => {
        setError(
          err.response?.data?.message ?? "Failed to save the client group",
        );
      });
  };

  return (
    <Box sx={defaultStyle}>
      <Typography variant="h5" component="h5">
        {group ? "Edit client group" : "Create a new client group"}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} sx={{ mt: "1rem" }}>
          <Grid item sm={12}>
            <TextField
              label="Name"
              size="small"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid item sm={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={visibleToClients}
                  onChange={(event) =>
                    setVisibleToClients(event.target.checked)
                  }
                />
              }
              label="Visible to clients"
            />
            <Typography variant="body2" sx={{ color: grey[600] }}>
              {visibleToClients
                ? "Members see this group and each other in their client report."
                : "Private: only NPM sees this group. Members see only their own report."}
            </Typography>
          </Grid>
          <Grid item sm={12}>
            <Autocomplete
              size="small"
              options={searchResults}
              value={null}
              inputValue={query}
              getOptionLabel={(option) => option.name}
              getOptionDisabled={(option) => memberIds.has(option.id)}
              filterOptions={(options) => options}
              onChange={(_event, value) => addMember(value)}
              onInputChange={(_event, value, reason) => {
                if (reason === "input") {
                  setQuery(value);
                }
              }}
              noOptionsText={query ? "No clients found" : "Type to search"}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <ListItemText
                    primary={option.name}
                    secondary={
                      memberIds.has(option.id)
                        ? "Already in this group"
                        : otherGroupNames(option.id) &&
                          `Also in ${otherGroupNames(option.id)}`
                    }
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add client"
                  placeholder="Search"
                />
              )}
            />
          </Grid>
          <Grid item sm={12}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Clients in this group ({members.length})
            </Typography>
            <Divider />
            {members.length > 0 ? (
              <List dense sx={{ maxHeight: 240, overflow: "auto" }}>
                {members.map((member) => (
                  <ListItem
                    key={member.id}
                    sx={{ px: 0.3 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`Remove ${member.name}`}
                        onClick={() => removeMember(member.id)}
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Link
                          href={`/${ProtectedPaths.Clients}/${member.id}`}
                          underline="hover"
                        >
                          {member.name}
                        </Link>
                      }
                      secondary={
                        otherGroupNames(member.id) &&
                        `Also in ${otherGroupNames(member.id)}`
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: grey[500], mt: 1, fontStyle: "italic" }}
              >
                No clients in this group
              </Typography>
            )}
          </Grid>
          {error && (
            <Grid item sm={12}>
              <Alert variant="filled" severity="error">
                {error}
              </Alert>
            </Grid>
          )}
          <Grid item sm={12} sx={{ mt: "1rem" }}>
            <Button
              variant="contained"
              type="submit"
              disabled={!name.trim()}
              sx={{ mr: "1rem" }}
            >
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ClientGroupModal;
